import { ComponentPropsWithRef, forwardRef } from 'react'
import { tss } from 'tss-react'
import { typography } from '~/common/styles/typography'
import { Input } from '~/components/ui/atoms/input/Input'
import { InputProps, InputVariant } from '~/components/ui/atoms/input/types'
import { getTextFieldColorPrimitives } from '~/components/ui/molecules/text-field/variants/primitives'

// Extend InputProps with the additional TextField-specific props
export interface TextFieldProps extends Omit<InputProps, 'value'> {
  helperText?: React.ReactNode
  longTextField?: boolean
}

// Create a new type that combines TextFieldProps with ComponentPropsWithRef,
// but omit conflicting props
type TextFieldComponentProps = TextFieldProps &
  Omit<ComponentPropsWithRef<'input'>, 'size'> & {
    isBorderless?: boolean
    removeRightRadius?: boolean
  }

export const TextField = forwardRef<HTMLInputElement, TextFieldComponentProps>(
  ({ label, helperText, type, className, isBorderless, size = 'small', ...props }, ref) => {
    const { cx, classes } = useStyles({
      variant: props.variant || 'primary',
      error: props.error,
      disabled: props.disabled,
      fullWidth: props.fullWidth,
      longTextField: props.longTextField,
      removeRightRadius: props.removeRightRadius,
    })

    return (
      <div className={cx(classes.root, className, isBorderless && '!border-none')}>
        {label && <div className={classes.label}>{label}</div>}
        <Input ref={ref} size={size} type={type} {...props} isBorderless={isBorderless} />
        {helperText && <div className={classes.helperText}>{helperText}</div>}
      </div>
    )
  },
)

TextField.displayName = 'TextField'

const useStyles = tss
  .withParams<{
    variant: InputVariant
    error?: boolean
    disabled?: boolean
    fullWidth?: boolean
    removeRightRadius?: boolean
  }>()
  .create(({ variant = 'primary', fullWidth, error, disabled, removeRightRadius }) => {
    const colorPrimitives = getTextFieldColorPrimitives({
      variant,
      error,
      disabled,
    })

    return {
      root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        ...(fullWidth && { width: '100%' }),
        fieldset: {
          borderRadius: variant === 'rounded' ? '4px' : '2px',
          ...(removeRightRadius && {
            borderTopRightRadius: '0 !important',
            borderBottomRightRadius: '0 !important',
          }),
        },
      },
      label: {
        ...colorPrimitives.label,
        ...typography.label,
      },
      helperText: {
        ...colorPrimitives.helperText,
        ...typography.label,
      },
    }
  })
